import { useMemo } from "react";
import { CommandItem } from "@volca/ui/components/ui/command";
import { useGetBusiness } from "@/business/query";
import { getErrorMessage } from "@/core/api/errors";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { formatDateRange } from "@/core/utils/date";
import { buildPeriods, DEFAULT_TEMPLATE } from "@/icm/icm-page/utils";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import { useGetPayPeriods } from "@/payroll/pay-periods/query";
import { isTech } from "@/user/utils";
import { CommandNestedState } from "./nested-list";
import type { CommandPage } from "./pages";

const hasCompletedOutput = (period: {
  latest_completed_ai_script_run_id: string | null;
  latest_completed_icm_run_id: string | null;
}) =>
  period.latest_completed_ai_script_run_id != null ||
  period.latest_completed_icm_run_id != null;

export const PayrollPeriodsPage = ({
  onOpen,
  onNavigate,
}: {
  onOpen: (page: CommandPage) => void;
  onNavigate: (to: string) => void;
}) => {
  const { businessId } = useBusiness();
  const { user } = useAuth();
  const business = useGetBusiness(businessId);
  const periodsQuery = useGetPayPeriods(businessId);
  const technician = isTech(user?.role);
  const template =
    business.data?.current_pay_period_template ?? DEFAULT_TEMPLATE;
  const { periods } = useMemo(
    () =>
      buildPeriods({
        template,
        payPeriods: periodsQuery.data ?? [],
        isBusinessLoading: business.isLoading,
        businessCreatedAt: business.data?.created_at,
      }),
    [
      template,
      periodsQuery.data,
      business.isLoading,
      business.data?.created_at,
    ],
  );
  const payPeriodByStart = useMemo(
    () =>
      new Map(
        (periodsQuery.data ?? []).map((period) => [period.start_date, period]),
      ),
    [periodsQuery.data],
  );
  const visible = periods.filter((period) => {
    if (!technician) return true;
    const payPeriod = payPeriodByStart.get(period.period_start);
    return !!payPeriod && hasCompletedOutput(payPeriod);
  });

  return (
    <CommandNestedState
      heading="Pay periods"
      loading={business.isLoading || periodsQuery.isLoading}
      error={
        periodsQuery.isError
          ? `Couldn't load pay periods. ${getErrorMessage(periodsQuery.error)}`
          : null
      }
      onRetry={() => void periodsQuery.refetch()}
      empty="No pay periods."
      count={visible.length}
    >
      {visible.map((period) => {
        const payPeriod = payPeriodByStart.get(period.period_start);
        const label = formatDateRange(period.period_start, period.period_end);
        const aiRunId = payPeriod?.latest_completed_ai_script_run_id ?? null;
        const icmRunId = payPeriod?.latest_completed_icm_run_id ?? null;
        return (
          <CommandItem
            key={period.period_start}
            value={`${label} ${period.period_start}`}
            onSelect={() => {
              if (technician || (!aiRunId && !icmRunId)) {
                onNavigate(getPayBreakdownPath(period.period_start));
                return;
              }
              onOpen({
                kind: "payroll-period",
                start: period.period_start,
                label,
                aiRunId,
                icmRunId,
              });
            }}
          >
            <span>{label}</span>
          </CommandItem>
        );
      })}
    </CommandNestedState>
  );
};
